'use client';

import React, { useEffect, useState } from 'react';
import { STATUS_URL, apiUrl } from '@/lib/config';

type SystemStatus = 'operational' | 'degraded' | 'down';

interface StatusBadgeProps {
  /** Optional custom endpoint URL for dynamic status checking */
  checkEndpoint?: string;
  /** Optional custom CSS classes */
  className?: string;
}

/**
 * StatusBadge Component
 *
 * Renders a high-contrast system status badge linking to status.postbrix.com.
 * Defaults immediately to "All Systems Operational" to prevent CLS and render delay.
 * Performs opt-in client-side status polling if `checkEndpoint` or `NEXT_PUBLIC_ENABLE_STATUS_CHECK` is provided.
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({ checkEndpoint, className = '' }) => {
  const [status, setStatus] = useState<SystemStatus>('operational');

  useEffect(() => {
    // Only attempt client-side dynamic polling if explicitly enabled
    const enabled = process.env.NEXT_PUBLIC_ENABLE_STATUS_CHECK === 'true' || Boolean(checkEndpoint);
    if (!enabled) return;

    const controller = new AbortController();
    const endpoint = checkEndpoint || apiUrl('/api/system-status');

    const fetchStatus = async () => {
      try {
        const res = await fetch(endpoint, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });

        if (!res.ok) return;

        const data = await res.json();
        const state = data?.data?.attributes?.aggregate_state || data?.status || data?.state;

        if (state === 'downtime' || state === 'major_outage' || state === 'down') {
          setStatus('down');
        } else if (state === 'degraded' || state === 'partial_outage') {
          setStatus('degraded');
        } else {
          setStatus('operational');
        }
      } catch {
        // Fallback silently to operational state on network/CORS failure
        setStatus('operational');
      }
    };

    void fetchStatus();

    // Refresh every 5 minutes to avoid main-thread overhead
    const intervalId = setInterval(fetchStatus, 300000);
    return () => {
      controller.abort();
      clearInterval(intervalId);
    };
  }, [checkEndpoint]);

  if (status === 'degraded') {
    return (
      <a
        target="_blank"
        href={STATUS_URL}
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 rounded-full border border-yellow-500/20 bg-yellow-500/10 px-3 py-1 text-xs font-medium text-yellow-700 dark:text-yellow-400 transition-colors hover:bg-yellow-500/20 ${className}`}
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-yellow-500" />
        </span>
        Partial System Outage
      </a>
    );
  }

  if (status === 'down') {
    return (
      <a
        target="_blank"
        href={STATUS_URL}
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-2 rounded-full border border-red-500/20 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-700 dark:text-red-400 transition-colors hover:bg-red-500/20 ${className}`}
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
        </span>
        Major System Outage
      </a>
    );
  }

  return (
    <a
      target="_blank"
      href={STATUS_URL}
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 transition-colors hover:bg-emerald-500/20 ${className}`}
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      All Systems Operational
    </a>
  );
};
